export type CampaignEmbeddedVideoProvider = 'youtube' | 'vimeo' | 'instagram' | 'tiktok';

export interface CampaignEmbeddedVideo {
  title?: string | null;
  embedUrl: string;
  provider: CampaignEmbeddedVideoProvider | string;
}

export interface CampaignEmbeddedVideoDraft {
  title: string;
  url: string;
}

export interface ParsedAdminEmbeddedVideo {
  title?: string;
  sourceUrl: string;
  embedUrl: string;
  provider: CampaignEmbeddedVideoProvider;
}

const IFRAME_OR_SCRIPT_PATTERN = /<\s*\/?\s*(iframe|script)\b/i;

export function containsUnsafeEmbeddedVideoMarkup(value: string): boolean {
  return IFRAME_OR_SCRIPT_PATTERN.test(value);
}

export function parseAdminEmbeddedVideosJson(embeddedVideosJson?: string | null): ParsedAdminEmbeddedVideo[] {
  if (!embeddedVideosJson?.trim()) {
    return [];
  }

  try {
    const parsed = JSON.parse(embeddedVideosJson) as unknown;
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed
      .map(item => {
        if (!item || typeof item !== 'object') {
          return null;
        }

        const record = item as Record<string, unknown>;
        const embedUrl = typeof record.embed_url === 'string' ? record.embed_url.trim() : '';
        const sourceUrl =
          typeof record.source_url === 'string'
            ? record.source_url.trim()
            : typeof record.url === 'string'
            ? record.url.trim()
            : embedUrl;
        const provider = typeof record.provider === 'string' ? record.provider.trim() : '';
        const title = typeof record.title === 'string' ? record.title.trim() : undefined;

        if (!embedUrl || !provider) {
          return null;
        }

        const video: ParsedAdminEmbeddedVideo = {
          sourceUrl: sourceUrl || embedUrl,
          embedUrl,
          provider: provider as CampaignEmbeddedVideoProvider,
        };

        if (title) {
          video.title = title;
        }

        return video;
      })
      .filter((video): video is ParsedAdminEmbeddedVideo => video !== null);
  } catch {
    return [];
  }
}

export function buildAdminEmbeddedVideosJson(drafts: CampaignEmbeddedVideoDraft[]): string {
  const payload = drafts
    .map(draft => ({
      title: draft.title.trim() || undefined,
      url: draft.url.trim(),
    }))
    .filter(item => item.url);

  return JSON.stringify(payload);
}

function getQueryValue(uri: URL, key: string): string | null {
  return uri.searchParams.get(key);
}

export function tryNormalizeEmbeddedVideoUrl(rawUrl: string): ParsedAdminEmbeddedVideo | null {
  const trimmed = rawUrl.trim();
  if (!trimmed || containsUnsafeEmbeddedVideoMarkup(trimmed)) {
    return null;
  }

  let uri: URL;
  try {
    uri = new URL(trimmed);
  } catch {
    return null;
  }

  if (uri.protocol !== 'https:') {
    return null;
  }

  const host = uri.hostname.toLowerCase();

  if (host === 'youtu.be') {
    const videoId = uri.pathname.replace(/^\/+/, '');
    return videoId ? createParsedVideo('youtube', videoId, trimmed) : null;
  }

  if (host === 'www.youtube.com' || host === 'youtube.com' || host === 'm.youtube.com') {
    const segments = uri.pathname.split('/').filter(Boolean);
    if (segments[0] === 'embed' && segments[1]) {
      return createParsedVideo('youtube', segments[1], trimmed);
    }
    if (segments[0] === 'shorts' && segments[1]) {
      return createParsedVideo('youtube', segments[1], trimmed);
    }
    if (segments[0] === 'watch') {
      const videoId = getQueryValue(uri, 'v');
      return videoId ? createParsedVideo('youtube', videoId, trimmed) : null;
    }
    return null;
  }

  if (host === 'www.youtube-nocookie.com' || host === 'youtube-nocookie.com') {
    const segments = uri.pathname.split('/').filter(Boolean);
    return segments[0] === 'embed' && segments[1] ? createParsedVideo('youtube', segments[1], trimmed) : null;
  }

  if (host === 'vimeo.com') {
    const segments = uri.pathname.split('/').filter(Boolean);
    return segments.length === 1 && /^\d+$/.test(segments[0]) ? createParsedVideo('vimeo', segments[0], trimmed) : null;
  }

  if (host === 'player.vimeo.com') {
    const segments = uri.pathname.split('/').filter(Boolean);
    return segments[0] === 'video' && segments[1] && /^\d+$/.test(segments[1])
      ? createParsedVideo('vimeo', segments[1], trimmed)
      : null;
  }

  if (host === 'www.instagram.com' || host === 'instagram.com') {
    const segments = uri.pathname.split('/').filter(Boolean);
    if (segments.length < 2 || !['reel', 'p', 'tv'].includes(segments[0])) {
      return null;
    }
    const contentId = segments[1];
    return contentId && contentId !== 'embed'
      ? createParsedVideo('instagram', `${segments[0]}/${contentId}`, trimmed)
      : null;
  }

  if (host === 'www.tiktok.com' || host === 'tiktok.com') {
    const segments = uri.pathname.split('/').filter(Boolean);
    if (segments[0] === 'embed' && segments[1] === 'v2' && segments[2] && /^\d+$/.test(segments[2])) {
      return createParsedVideo('tiktok', segments[2], trimmed);
    }
    const videoIndex = segments.findIndex(segment => segment === 'video');
    if (videoIndex >= 0 && segments[videoIndex + 1] && /^\d+$/.test(segments[videoIndex + 1])) {
      return createParsedVideo('tiktok', segments[videoIndex + 1], trimmed);
    }
  }

  return null;
}

function createParsedVideo(
  provider: CampaignEmbeddedVideoProvider,
  resourceId: string,
  sourceUrl: string
): ParsedAdminEmbeddedVideo {
  const embedUrl =
    provider === 'youtube'
      ? `https://www.youtube-nocookie.com/embed/${resourceId}`
      : provider === 'vimeo'
      ? `https://player.vimeo.com/video/${resourceId}`
      : provider === 'instagram'
      ? `https://www.instagram.com/${resourceId}/embed`
      : `https://www.tiktok.com/embed/v2/${resourceId}`;

  return {
    sourceUrl,
    embedUrl,
    provider,
  };
}

export function mapPublicEmbeddedVideos(videos?: CampaignEmbeddedVideo[] | null): CampaignEmbeddedVideo[] {
  return (videos ?? []).filter(video => Boolean(video.embedUrl?.trim()));
}
